/**
 * Client-side wrapper for the ai-proxy Supabase Edge Function.
 * Replaces all direct OpenAI SDK calls — the API key never touches the browser.
 */
import { supabase } from '../config/supabase';
import { syncCreditsFromResponse } from '../utils/credits';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const PROXY_URL = `${SUPABASE_URL}/functions/v1/ai-proxy`;

/* ─── Auth headers ─── */

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('NOT_AUTHENTICATED');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`,
    'apikey': SUPABASE_ANON_KEY,
  };
}

/* ─── Error handling ─── */

function isAbortError(err) {
  return err.name === 'AbortError' || err.name === 'APIUserAbortError' || err.code === 'ABORT_ERR';
}

async function handleErrorResponse(res) {
  const body = await res.json().catch(() => ({}));

  // Sync credits from error responses too
  if (body.credits_remaining != null) {
    syncCreditsFromResponse(body.credits_remaining);
  }

  const err = new Error(body.error || `AI proxy error (${res.status})`);
  err.code = body.code || 'UNKNOWN';
  err.status = res.status;
  throw err;
}

/* ─── Non-streaming call ─── */

/**
 * Make a non-streaming AI call through the proxy.
 *
 * @param {Object} body  - { action, creditCost, model, messages, tools?, temperature?, max_tokens?, maxToolRounds?, timeout? }
 * @param {Object} opts  - { signal?, timeout? }
 * @returns {{ result: string, credits_remaining: number }}
 */
export async function aiCall(body, { signal, timeout = 30000 } = {}) {
  const headers = await getAuthHeaders();

  // Use caller's signal or create our own with timeout
  const controller = signal ? undefined : new AbortController();
  const effectiveSignal = signal || controller?.signal;
  const timer = controller ? setTimeout(() => controller.abort(), timeout) : undefined;

  try {
    const res = await fetch(PROXY_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...body, timeout }),
      signal: effectiveSignal,
    });

    if (!res.ok) await handleErrorResponse(res);

    const data = await res.json();
    if (data.credits_remaining != null) {
      syncCreditsFromResponse(data.credits_remaining);
    }
    return data;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/* ─── Streaming call ─── */

/**
 * Make a streaming AI call through the proxy.
 * Returns the fetch Response for the caller to read as an SSE stream.
 *
 * SSE events:
 *   data: {"delta":"..."}           — content token
 *   data: [DONE]                    — stream complete
 *   data: {"credits_remaining": N}  — final credit count
 *   data: {"error":"..."}           — stream error
 *
 * @param {Object} body  - { creditCost, model, messages, temperature?, max_tokens? }
 * @param {Object} opts  - { signal? }
 * @returns {Response} — the raw fetch Response with SSE body
 */
export async function aiStream(body, { signal } = {}) {
  const headers = await getAuthHeaders();

  const res = await fetch(PROXY_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ...body, action: 'chat_stream' }),
    signal,
  });

  if (!res.ok) await handleErrorResponse(res);

  return res;
}

export { isAbortError };
